'use client';

import { Link } from '@/lib/i18n/navigation';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

import { Icon, LinkButton, Tag } from '@/components/ui';

import { NewsPage } from '@/lib/contentful/news';
import styles from './StoryCard.module.scss';

export const StoryCard = ({
  pageTitle,
  shortDescription,
  thumbnail,
  tagsCollection,
  slug,
}: NewsPage) => {
  const t = useTranslations('product');

  const imageUrl = thumbnail?.image?.url;
  const imageAlt = thumbnail?.alt ?? '';
  const tag = tagsCollection?.items?.[0]?.value;
  const hasCta = Boolean(slug);

  if (!imageUrl) return null;

  return (
    <article className={clsx(styles.card)}>
      {hasCta && (
        <Link href={`/${slug}`} className={styles.cardLink} tabIndex={-1} aria-hidden="true" />
      )}
      <Image
        src={imageUrl}
        alt={imageAlt}
        fill
        className={styles.image}
        // CMS news thumbnails are often much wider than this 2:3 card; object-fit: cover
        // on a mismatched aspect ratio made Next serve transforms that looked pixelated.
        // Inflated sizes force a higher-res download. Tighten toward 100vw / ~25vw once
        // Contentful assets are cropped to the card ratio (smaller files, better LCP).
        sizes="(max-width: 768px) 150vw, 60vw"
      />

      <div className={styles.overlay} />

      {tag && (
        <div className={styles.tagWrapper}>
          <Tag label={tag} variant="accent" className={styles.tag} />
        </div>
      )}

      <div className={styles.content}>
        {shortDescription && <span className={styles.subtitle}>{shortDescription}</span>}
        {pageTitle && <h3 className={styles.title}>{pageTitle}</h3>}
        {hasCta && (
          <div className={styles.cta}>
            <LinkButton
              href={`/${slug}`}
              variant="link"
              color="white"
              iconAfter={<Icon name="chevron-right" size={16} />}
              className={styles.ctaButton}
            >
              {t('cta.discover')}
            </LinkButton>
          </div>
        )}
      </div>
    </article>
  );
};

StoryCard.displayName = 'StoryCard';
